/** Diálogo de ayuda — PAT ThinQ Connect en SHC. */

import React from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';

type Props = {
  open: boolean;
  onClose: () => void;
};

const HelpBlock: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ mb: 1.05 }}>
    <Typography sx={{ color: '#2391FF', fontSize: '0.8rem', fontWeight: 'bold', mb: 0.2, lineHeight: 1.25 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const SETUP_KEYS = ['setup1', 'setup2', 'setup3', 'setup4', 'setup5'] as const;
const USE_KEYS = ['use1', 'use2', 'use3', 'use4'] as const;

const LgSetupHelpDialog: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const requestClose = useOverlayBackButton(open, onClose, 'lg-setup-help');

  return (
    <Dialog
      open={open}
      onClose={requestClose}
      scroll="body"
      PaperProps={{
        style: {
          backgroundColor: '#1E1E1E',
          color: 'white',
          minWidth: '320px',
          maxWidth: '600px',
          width: '92vw',
          margin: 16,
        },
      }}
    >
      <DialogTitle sx={{
        color: '#2391FF', fontSize: '0.98rem', fontWeight: 'bold',
        py: 1.35, px: 2, pb: 0.75,
      }}
      >
        {t('settings.lgDiscovery.help.title', { defaultValue: 'Conectar LG ThinQ Connect' })}
      </DialogTitle>
      <DialogContent sx={{
        pt: '8px !important',
        pb: 1,
        px: 2,
        overflow: 'visible',
        '&.MuiDialogContent-root': { paddingTop: '8px' },
      }}
      >
        <Typography sx={{ color: '#ccc', fontSize: '0.78rem', lineHeight: 1.4, mb: 1.15 }}>
          {t('settings.lgDiscovery.help.intro', {
            defaultValue: 'SHC se conecta a la nube oficial ThinQ Connect con un PAT personal y el código de país del hogar.',
          })}
        </Typography>

        <HelpBlock title={t('settings.lgDiscovery.help.setupTitle', { defaultValue: 'Obtener PAT' })}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.65 }}>
            {SETUP_KEYS.map((key, i) => (
              <Box key={key} sx={{ display: 'flex', gap: 0.7, alignItems: 'flex-start' }}>
                <Chip
                  size="small"
                  label={i + 1}
                  sx={{
                    bgcolor: '#2391FF', color: '#0b0b0b', fontWeight: 700,
                    height: 19, minWidth: 23, fontSize: '0.68rem', flexShrink: 0, mt: 0.05,
                  }}
                />
                <Typography sx={{ color: '#ccc', fontSize: '0.75rem', lineHeight: 1.36 }}>
                  {t(`settings.lgDiscovery.help.${key}`, { defaultValue: '' })}
                </Typography>
              </Box>
            ))}
          </Box>
        </HelpBlock>

        <Divider sx={{ borderColor: '#333', my: 1.1 }} />

        <HelpBlock title={t('settings.lgDiscovery.help.useTitle', { defaultValue: 'En SHC' })}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.65 }}>
            {USE_KEYS.map((key, i) => (
              <Box key={key} sx={{ display: 'flex', gap: 0.7, alignItems: 'flex-start' }}>
                <Chip
                  size="small"
                  label={i + 1}
                  sx={{
                    bgcolor: '#2391FF', color: '#0b0b0b', fontWeight: 700,
                    height: 19, minWidth: 23, fontSize: '0.68rem', flexShrink: 0, mt: 0.05,
                  }}
                />
                <Typography sx={{ color: '#ccc', fontSize: '0.75rem', lineHeight: 1.36 }}>
                  {t(`settings.lgDiscovery.help.${key}`, { defaultValue: '' })}
                </Typography>
              </Box>
            ))}
          </Box>
        </HelpBlock>
      </DialogContent>
      <DialogActions sx={{ py: 1.15, px: 2, pt: 0 }}>
        <Button
          onClick={requestClose}
          sx={{
            color: '#2391FF',
            fontWeight: 'bold',
            '&:hover': { backgroundColor: 'rgba(35, 145, 255, 0.1)' },
          }}
        >
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LgSetupHelpDialog;
